/**
 * Sales-level (Cisco territory) grouping for campaign reporting.
 *
 * The global reporting pages group campaign activity by **Sales Level 1** (theatre, e.g. Americas/EMEA/
 * APJC) and, when a level-1 is selected, drill into **Sales Level 2** (the sub-region under it). These
 * are `User.sales_level_1`/`sales_level_2` — free-text territory strings synced from Salesforce.
 *
 * Two populations get grouped, and they resolve their sales level differently:
 *  - **Enrollments** carry the SE (`user` id + `user_email`), so they join straight to the user index.
 *  - **Deliveries** have no SE FK; the reporting-redesign adds a backend `owner_user` link that
 *    surfaces read-only `owner_sales_level_1`/`owner_sales_level_2` on the delivery. We prefer those
 *    denormalized fields and fall back to matching the free-text `owner` (an email) against the user
 *    index — so grouping works before that backend change deploys (dev/uat Go, prod Django) and on
 *    rows the backfill hasn't matched.
 *
 * Everything here is pure and framework-free — callers pass already-fetched rows.
 */
import {
  buildStepTree,
  visibleSteps,
  campaignStops,
  deliveryStops,
  isPerDeliveryGroup,
  enrollmentStatus,
  type CampaignDelivery,
  type CampaignDeliveryStepStatus,
  type CampaignEnrollment,
  type CampaignStep,
} from './campaigns';
import type { MetricRow } from './campaignAnalytics';
import { deliveryPipeline } from './campaignLeader';
import {
  WORKSHOP_SCHEDULE_STATUSES,
  WorkshopScheduleStatus,
  deliveryWorkshopStatus,
} from './campaignWorkshopStatus';
import type { CampaignDeliveryTarget, CampaignWorkshopMeeting } from './campaigns';

/** Bucket for users/deliveries whose territory is blank or unresolved. */
export const UNASSIGNED = 'Unassigned';

/**
 * The catch-all Sales-Level-1 bucket, for the "whole world / no specific territory" case. A level-1
 * value collapses to `Global` only when it either covers **all three theatres at once** — the combined
 * `Americas,APJC,EMEA` string a global role carries — **or** names no territory at all (blank, or only
 * `Unassigned`). A single theatre (`Americas`) or a genuine region (`US COMMERCIAL`) is a real
 * assignment and stays its own group. Matched case-insensitively.
 */
export const GLOBAL = 'Global';
const THEATRES = ['americas', 'apjc', 'emea'];
const canonicalSl1 = (raw: string): string => {
  const t = raw.trim();
  const parts = t
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  // "none exist": blank, or only the Unassigned placeholder — no territory named.
  const named = parts.filter((p) => p !== 'unassigned');
  if (named.length === 0) return GLOBAL;
  // "all three covered": every theatre present in one value (e.g. "Americas,APJC,EMEA").
  if (THEATRES.every((th) => parts.includes(th))) return GLOBAL;
  return t;
};

/** A resolved (level-1, level-2, level-3) territory pair; blanks collapse to `UNASSIGNED`. */
export interface SalesLevelPair {
  sl1: string;
  sl2: string;
  sl3: string;
}

/** The minimal user shape we need from the `db/user` list (extra fields ignored). */
export interface SalesLevelUser {
  id?: number;
  email?: string | null;
  sales_level_1?: string | null;
  sales_level_2?: string | null;
  sales_level_3?: string | null;
}

/** Lookups from an SE's identity to their territory pair. */
export interface SalesLevelIndex {
  byEmail: Map<string, SalesLevelPair>;
  byId: Map<number, SalesLevelPair>;
}

/**
 * Which drill the reporting page is at: none = Global; `sl1` set = a theatre; `sl1`+`sl2` = a region;
 * `sl1`+`sl2`+`sl3` = the sub-region under it (the terminal level).
 */
export interface SalesLevelDrill {
  sl1?: string;
  sl2?: string;
  sl3?: string;
}

const norm = (v: string | null | undefined): string => (v ?? '').trim();
const pair = (sl1?: string | null, sl2?: string | null, sl3?: string | null): SalesLevelPair => ({
  sl1: canonicalSl1(norm(sl1)),
  sl2: norm(sl2) || UNASSIGNED,
  sl3: norm(sl3) || UNASSIGNED,
});

/** Build the email/id → territory lookups from the user list. Emails are keyed lowercased. */
export function buildSalesLevelIndex(users: SalesLevelUser[]): SalesLevelIndex {
  const byEmail = new Map<string, SalesLevelPair>();
  const byId = new Map<number, SalesLevelPair>();
  for (const u of users || []) {
    const p = pair(u.sales_level_1, u.sales_level_2, u.sales_level_3);
    const email = norm(u.email).toLowerCase();
    if (email) byEmail.set(email, p);
    if (typeof u.id === 'number') byId.set(u.id, p);
  }
  return { byEmail, byId };
}

/** An enrollment's territory: match on `user_email`, else the numeric `user` id, else `Unassigned`. */
export function enrollmentSalesLevel(index: SalesLevelIndex, e: CampaignEnrollment): SalesLevelPair {
  const email = norm(e.user_email).toLowerCase();
  if (email && index.byEmail.has(email)) return index.byEmail.get(email) as SalesLevelPair;
  if (typeof e.user === 'number' && index.byId.has(e.user)) {
    return index.byId.get(e.user) as SalesLevelPair;
  }
  return pair(null, null);
}

/**
 * A delivery's territory: prefer the backend `owner_sales_level_1/2` denormalization when present,
 * else match the free-text `owner` (an email) against the user index, else `Unassigned`.
 */
export function deliverySalesLevel(
  index: SalesLevelIndex,
  d: CampaignDelivery & {
    owner_sales_level_1?: string | null;
    owner_sales_level_2?: string | null;
    owner_sales_level_3?: string | null;
  },
): SalesLevelPair {
  if (norm(d.owner_sales_level_1) || norm(d.owner_sales_level_2) || norm(d.owner_sales_level_3)) {
    return pair(d.owner_sales_level_1, d.owner_sales_level_2, d.owner_sales_level_3);
  }
  const owner = norm(d.owner).toLowerCase();
  if (owner && index.byEmail.has(owner)) return index.byEmail.get(owner) as SalesLevelPair;
  return pair(null, null);
}

/** Does a pair fall within the current drill? (no drill = everything.) */
export function matchesDrill(p: SalesLevelPair, drill: SalesLevelDrill): boolean {
  if (drill.sl1 != null && p.sl1 !== drill.sl1) return false;
  if (drill.sl2 != null && p.sl2 !== drill.sl2) return false;
  if (drill.sl3 != null && p.sl3 !== drill.sl3) return false;
  return true;
}

/**
 * The grouping key for the *outer ring / bar categories* at a given drill — always one level below the
 * current filter: level-1 while Global, level-2 once a level-1 is selected, level-3 once a level-2 is
 * selected. (Drilling past level-3 keeps splitting by level-3, i.e. one bucket — the terminal view.)
 */
export function groupKeyFor(p: SalesLevelPair, drill: SalesLevelDrill): string {
  if (drill.sl1 == null) return p.sl1;
  if (drill.sl2 == null) return p.sl2;
  return p.sl3;
}

/** Sorted unique values, with the catch-all buckets (`Global`, then `Unassigned`) always last. */
function sortedLevels(values: Iterable<string>): string[] {
  const set = new Set(values);
  const tail: string[] = [];
  for (const special of [GLOBAL, UNASSIGNED]) {
    if (set.delete(special)) tail.push(special);
  }
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .concat(tail);
}

/** The level-1 options present across a set of pairs (for the top-level select). */
export function level1Options(pairs: SalesLevelPair[]): string[] {
  return sortedLevels(pairs.map((p) => p.sl1));
}

/** The level-2 options present under a chosen level-1 (for the drill select). */
export function level2Options(pairs: SalesLevelPair[], sl1: string): string[] {
  return sortedLevels(pairs.filter((p) => p.sl1 === sl1).map((p) => p.sl2));
}

/** The level-3 options present under a chosen level-1 + level-2 (for the drill select). */
export function level3Options(pairs: SalesLevelPair[], sl1: string, sl2: string): string[] {
  return sortedLevels(pairs.filter((p) => p.sl1 === sl1 && p.sl2 === sl2).map((p) => p.sl3));
}

// ---------------------------------------------------------------------------
// Req 1 — one-time (enablement) completion: counts + two-ring donut
// ---------------------------------------------------------------------------

/** Enrollment status buckets, in the order the inner ring / KPI row uses them. */
const ENROLL_STATUS_ORDER: { value: CampaignEnrollment['status']; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

/** In-scope enrollments after applying the sales-level drill. */
export function enrollmentsInDrill(
  enrollments: CampaignEnrollment[],
  index: SalesLevelIndex,
  drill: SalesLevelDrill,
): CampaignEnrollment[] {
  return enrollments.filter((e) => matchesDrill(enrollmentSalesLevel(index, e), drill));
}

/** Headline In-Progress / Completed counts (+ total, not-started) for the in-scope enrollments. */
export interface CompletionCounts {
  total: number;
  notStarted: number;
  inProgress: number;
  completed: number;
}

export function completionCounts(enrollments: CampaignEnrollment[]): CompletionCounts {
  const c: CompletionCounts = { total: enrollments.length, notStarted: 0, inProgress: 0, completed: 0 };
  for (const e of enrollments) {
    // Classify on the derived, composite-aware rollup (progress.status), not the stale stored column.
    const st = enrollmentStatus(e);
    if (st === 'completed') c.completed += 1;
    else if (st === 'in_progress') c.inProgress += 1;
    else c.notStarted += 1;
  }
  return c;
}

/**
 * The nested completion donut for the in-scope enrollments — a hierarchy, not two independent totals:
 *  - **outer** ring = the population split by the current grouping (level-1 while Global, else
 *    level-2/level-3) — the group each enrollment belongs to;
 *  - **inner** ring = each outer group's slice subdivided by enrollment status (Not Started / In
 *    Progress / Completed).
 * The inner rows are emitted in the SAME group order as the outer rows, and each group's status
 * slices sum to that group's outer value — so, drawn as two concentric donuts in data order, the
 * inner status slices sit angularly under their group's outer slice (a sunburst built from two rings).
 * Both rings still sum to the same `total`. Inner-slice labels carry `Group · Status` so each slice is
 * uniquely keyed (the renderer keys slices by label); the runner strips the `Group · ` prefix in the
 * tooltip so a hover names just the status.
 */
export interface TwoRingSeries {
  inner: MetricRow[];
  outer: MetricRow[];
  total: number;
}

/** The `Group · Status` composite-label delimiter — the tooltip shows only the trailing status. */
export const GROUP_STATUS_DELIM = ' · ';

/** Build the aligned outer (group) + inner (group→status) rings from a per-group status breakdown. */
function twoRingFromGroupStatus(
  byGroupStatus: Map<string, Map<string, number>>,
  groupTotals: Map<string, number>,
  total: number,
): TwoRingSeries {
  const groups = sortedLevels(groupTotals.keys());
  const outer = groups.map((label) => ({ label, value: groupTotals.get(label) ?? 0 }));
  const inner: MetricRow[] = [];
  for (const group of groups) {
    const statuses = byGroupStatus.get(group);
    for (const s of ENROLL_STATUS_ORDER) {
      const value = statuses?.get(s.value) ?? 0;
      // Drop empty status slices — they contribute no angle, so removing them keeps the inner ring
      // aligned to the outer group boundaries while avoiding zero-width wedges.
      if (value > 0) inner.push({ label: `${group}${GROUP_STATUS_DELIM}${s.label}`, value });
    }
  }
  return { inner, outer, total };
}

export function twoRingCompletion(
  enrollments: CampaignEnrollment[],
  index: SalesLevelIndex,
  drill: SalesLevelDrill,
): TwoRingSeries {
  const scoped = enrollmentsInDrill(enrollments, index, drill);

  const byGroupStatus = new Map<string, Map<string, number>>();
  const groupTotals = new Map<string, number>();
  for (const e of scoped) {
    // Derived, composite-aware rollup — not the stale stored `status` column (matches completionCounts).
    const st = enrollmentStatus(e);
    const key = groupKeyFor(enrollmentSalesLevel(index, e), drill);
    let statuses = byGroupStatus.get(key);
    if (!statuses) {
      statuses = new Map<string, number>();
      byGroupStatus.set(key, statuses);
    }
    statuses.set(st, (statuses.get(st) ?? 0) + 1);
    groupTotals.set(key, (groupTotals.get(key) ?? 0) + 1);
  }

  return twoRingFromGroupStatus(byGroupStatus, groupTotals, scoped.length);
}

// ---------------------------------------------------------------------------
// Req 2 — delivery progression: farthest-progress-per-delivery, hidden steps excluded
// ---------------------------------------------------------------------------

/** An ordered per-delivery step sequence and the lookups a farthest-progress calc needs. */
export interface DeliverySequence {
  /** Step ids in walk order (the leaf sub-steps every delivery walks; hidden steps excluded). */
  orderedStepIds: number[];
  /** step id → 0-based position in the sequence. */
  indexByStep: Map<number, number>;
  /** step id → display name. */
  labelByStep: Map<number, string>;
}

/**
 * The ordered sequence of steps a delivery walks — the leaf sub-steps of every top-level per-delivery
 * group, flattened in order. **Hidden steps (and steps under a hidden group) are excluded** via
 * `visibleSteps`, so completion/progress metrics never count a step the runner would not show.
 *
 * `CampaignStep.order` is per-group, not global, so a raw max-of-order is wrong across groups; this
 * builds the true global walk order the runner uses (`buildStepTree` → per-delivery groups →
 * `deliveryStops`).
 */
export function deliverySequence(steps: CampaignStep[]): DeliverySequence {
  const tree = buildStepTree(visibleSteps(steps));
  const orderedStepIds: number[] = [];
  const labelByStep = new Map<number, string>();
  for (const root of campaignStops(tree)) {
    if (isPerDeliveryGroup(root)) {
      for (const leaf of deliveryStops(root)) {
        orderedStepIds.push(leaf.id);
        labelByStep.set(leaf.id, leaf.name);
      }
    }
  }
  const indexByStep = new Map<number, number>(orderedStepIds.map((id, i) => [id, i]));
  return { orderedStepIds, indexByStep, labelByStep };
}

/**
 * The farthest sequence position a delivery reached — the max index (over its `in_progress`/`completed`
 * step-status rows) into the visible sequence. Steps not in the sequence (hidden, or not part of the
 * per-delivery walk) are ignored. Returns `null` when the delivery has made no progress.
 */
export function deliveryFarthestIndex(
  statuses: CampaignDeliveryStepStatus[],
  seq: DeliverySequence,
): number | null {
  let far: number | null = null;
  for (const s of statuses) {
    if (s.status !== 'in_progress' && s.status !== 'completed') continue;
    const idx = seq.indexByStep.get(s.step);
    if (idx == null) continue;
    if (far == null || idx > far) far = idx;
  }
  return far;
}

/**
 * Counts of deliveries by their farthest-reached stop — a progression funnel. Deliveries with no
 * progress land in a leading "Not started" bucket. Only the passed `deliveries` are counted (the
 * caller applies the sales-level drill first), and `statusesByDelivery` supplies each delivery's
 * step-status rows.
 */
export function farthestProgressDistribution(
  deliveries: CampaignDelivery[],
  seq: DeliverySequence,
  statusesByDelivery: Map<number, CampaignDeliveryStepStatus[]>,
): MetricRow[] {
  const notStartedLabel = 'Not started';
  const counts = new Map<string, number>();
  counts.set(notStartedLabel, 0);
  for (const id of seq.orderedStepIds) counts.set(seq.labelByStep.get(id) ?? `Step #${id}`, 0);

  for (const d of deliveries) {
    const far = deliveryFarthestIndex(statusesByDelivery.get(d.id) ?? [], seq);
    if (far == null) {
      counts.set(notStartedLabel, (counts.get(notStartedLabel) ?? 0) + 1);
    } else {
      const stepId = seq.orderedStepIds[far];
      const label = seq.labelByStep.get(stepId) ?? `Step #${stepId}`;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
  }

  const rows: MetricRow[] = [{ label: notStartedLabel, value: counts.get(notStartedLabel) ?? 0 }];
  for (const id of seq.orderedStepIds) {
    const label = seq.labelByStep.get(id) ?? `Step #${id}`;
    rows.push({ label, value: counts.get(label) ?? 0 });
  }
  return rows;
}

/**
 * Delivery progression as a **stacked bar by sales-level cohort**: one wide row per cohort (level-1
 * while Global, level-2 once a level-1 is filtered, level-3 once a level-2 is filtered — see
 * {@link groupKeyFor}), each carrying a count for every progression stage ("Not started" + one per
 * visible step in walk order). Rows are pre-pivoted **wide** and 0-fill every stage so the inline
 * chart renderer always sees ≥2 numeric keys and draws a stack (stack/legend order follows the
 * `stages` insertion order). Deliveries are filtered to the drill internally by owner territory.
 *
 * Returns the `rows` (chart data), the ordered `stages` (stack keys), and `maxTotal` (the tallest
 * stacked column) so the caller can build integer axis ticks.
 */
export interface CohortProgress {
  rows: Array<Record<string, string | number>>;
  stages: string[];
  maxTotal: number;
}

export function farthestProgressByCohort(
  deliveries: (CampaignDelivery & {
    owner_sales_level_1?: string | null;
    owner_sales_level_2?: string | null;
    owner_sales_level_3?: string | null;
  })[],
  seq: DeliverySequence,
  statusesByDelivery: Map<number, CampaignDeliveryStepStatus[]>,
  index: SalesLevelIndex,
  drill: SalesLevelDrill,
): CohortProgress {
  const notStartedLabel = 'Not started';
  const stages = [notStartedLabel, ...seq.orderedStepIds.map((id) => seq.labelByStep.get(id) ?? `Step #${id}`)];

  // cohort → (stage → count)
  const byCohort = new Map<string, Map<string, number>>();
  for (const d of deliveries) {
    const p = deliverySalesLevel(index, d);
    if (!matchesDrill(p, drill)) continue;
    const cohort = groupKeyFor(p, drill);
    const far = deliveryFarthestIndex(statusesByDelivery.get(d.id) ?? [], seq);
    const stage =
      far == null
        ? notStartedLabel
        : seq.labelByStep.get(seq.orderedStepIds[far]) ?? `Step #${seq.orderedStepIds[far]}`;
    const bucket = byCohort.get(cohort) ?? new Map<string, number>();
    bucket.set(stage, (bucket.get(stage) ?? 0) + 1);
    byCohort.set(cohort, bucket);
  }

  let maxTotal = 0;
  const rows = sortedLevels(byCohort.keys()).map((cohort) => {
    const bucket = byCohort.get(cohort) ?? new Map<string, number>();
    // Insert `label` first, then every stage in walk order (0-filled) — guarantees the wide/stacked
    // inline render path and a stable stack order.
    const row: Record<string, string | number> = { label: cohort };
    let total = 0;
    for (const stage of stages) {
      const count = bucket.get(stage) ?? 0;
      row[stage] = count;
      total += count;
    }
    if (total > maxTotal) maxTotal = total;
    return row;
  });

  return { rows, stages, maxTotal };
}

/** In-scope deliveries after applying the sales-level drill (by owner territory). */
export function deliveriesInDrill(
  deliveries: (CampaignDelivery & { owner_sales_level_1?: string | null; owner_sales_level_2?: string | null })[],
  index: SalesLevelIndex,
  drill: SalesLevelDrill,
): CampaignDelivery[] {
  return deliveries.filter((d) => matchesDrill(deliverySalesLevel(index, d), drill));
}

// ---------------------------------------------------------------------------
// Req 5 — pipeline $ relative to delivery status, with counts (incl. no-opportunity)
// ---------------------------------------------------------------------------

/** One delivery-status row: how many deliveries, how much attributed pipeline, and how many have no
 *  opportunity attached yet (future-pipeline sessions that still count). */
export interface StatusPipelineRow {
  status: CampaignDelivery['status'];
  label: string;
  count: number;
  amount: number;
  /** Of `count`, how many have no linked opportunity — the sessions with $0 attributed so far. */
  noOppCount: number;
}

/**
 * Pipeline $ **and** delivery counts bucketed by delivery status. Every delivery is counted in its
 * status bucket regardless of whether an opportunity is attached (the reframe: session volume is the
 * leading indicator; the attributed $ is context), and `noOppCount` surfaces how many of those
 * sessions have no opportunity linked yet. `targetsByDelivery`/`amountByOpp` come from
 * `useCampaignPipeline`; a delivery with no linked opp contributes 0 to `amount`.
 */
export function statusPipelineRows(
  deliveries: CampaignDelivery[],
  statusOrder: { value: CampaignDelivery['status']; label: string }[],
  targetsByDelivery: Map<number, import('./campaigns').CampaignDeliveryTarget[]>,
  amountByOpp: Map<string, number>,
): StatusPipelineRow[] {
  const rows = new Map<CampaignDelivery['status'], StatusPipelineRow>(
    statusOrder.map((s) => [s.value, { status: s.value, label: s.label, count: 0, amount: 0, noOppCount: 0 }]),
  );
  for (const d of deliveries) {
    const row = rows.get(d.status);
    if (!row) continue;
    row.count += 1;
    const amount = deliveryPipeline(d, targetsByDelivery, amountByOpp);
    row.amount += amount;
    const targets = targetsByDelivery.get(d.id) ?? [];
    const hasOpp = targets.some((t) => !!t.opportunity);
    if (!hasOpp) row.noOppCount += 1;
  }
  return statusOrder.map((s) => rows.get(s.value) as StatusPipelineRow);
}

// ---------------------------------------------------------------------------
// Server-aggregated completion cohorts (the /api/db/campaign/{pk}/completion endpoint)
//
// For enablement completion at scale (up to ~10k enrollments) the frontend can no longer fetch every
// enrollment row — the generic list API caps a page below that. Instead the Go endpoint tallies the
// composite-aware rollup server-side and returns pre-grouped cohorts: one bucket per raw (sl1,sl2,sl3)
// territory tuple (by=territory) or per owner email (by=owner). Canonicalisation stays HERE — the server
// returns only trimmed raw levels, and these helpers apply the SAME `pair()`/`canonicalSl1` collapse and
// the SAME drill/`groupKeyFor` grouping the enrollment-row path uses, so both paths agree bucket-for-bucket
// and a consumer can fall back to the client path without the numbers shifting.
// ---------------------------------------------------------------------------

/** One server cohort: a raw territory tuple (+ optional owner email) with its completion counts and the
 *  sum of per-enrollment rollup percent (so mean completion = `percent_sum / enrolled`). */
export interface CompletionCohort {
  sl1: string;
  sl2: string;
  sl3: string;
  email?: string;
  enrolled: number;
  not_started: number;
  in_progress: number;
  completed: number;
  percent_sum: number;
}

/** The full server response for `GET db/campaign/{pk}/completion/`. `scope` is `org` when a subtree
 *  filter was applied (own subtree, by=owner, or a manager param), `global` when the whole org was
 *  tallied. `by=owner` cohorts additionally carry `email`. */
export interface CampaignCompletionResponse {
  campaign: number;
  by: 'territory' | 'owner';
  scope: 'global' | 'org';
  manager?: string;
  total: { enrolled: number; not_started: number; in_progress: number; completed: number; percent_sum: number };
  cohorts: CompletionCohort[];
}

/** A cohort's canonical territory pair (same collapse as an enrollment's). */
export function cohortSalesLevel(c: CompletionCohort): SalesLevelPair {
  return pair(c.sl1, c.sl2, c.sl3);
}

/** The distinct territory pairs present across cohorts — feeds level1/2/3Options for the drill selects. */
export function cohortPairs(cohorts: CompletionCohort[]): SalesLevelPair[] {
  return (cohorts || []).map(cohortSalesLevel);
}

/** Cohorts falling within the current drill (by canonical territory). */
export function cohortsInDrill(cohorts: CompletionCohort[], drill: SalesLevelDrill): CompletionCohort[] {
  return (cohorts || []).filter((c) => matchesDrill(cohortSalesLevel(c), drill));
}

/** Headline completion counts for the in-scope cohorts — the server-aggregated peer of {@link completionCounts}. */
export function completionCountsFromCohorts(
  cohorts: CompletionCohort[],
  drill: SalesLevelDrill,
): CompletionCounts {
  const c: CompletionCounts = { total: 0, notStarted: 0, inProgress: 0, completed: 0 };
  for (const co of cohortsInDrill(cohorts, drill)) {
    c.total += co.enrolled;
    c.notStarted += co.not_started;
    c.inProgress += co.in_progress;
    c.completed += co.completed;
  }
  return c;
}

/**
 * The nested completion donut from server cohorts — the peer of {@link twoRingCompletion}. Outer ring
 * splits the in-scope population by the current grouping (level-1 while Global, else level-2/level-3);
 * inner ring subdivides each group's slice by status, emitted in the same group order so the status
 * slices align angularly under their group's outer wedge.
 */
export function twoRingCompletionFromCohorts(
  cohorts: CompletionCohort[],
  drill: SalesLevelDrill,
): TwoRingSeries {
  const scoped = cohortsInDrill(cohorts, drill);

  const byGroupStatus = new Map<string, Map<string, number>>();
  const groupTotals = new Map<string, number>();
  let total = 0;
  for (const co of scoped) {
    const key = groupKeyFor(cohortSalesLevel(co), drill);
    let statuses = byGroupStatus.get(key);
    if (!statuses) {
      statuses = new Map<string, number>();
      byGroupStatus.set(key, statuses);
    }
    statuses.set('not_started', (statuses.get('not_started') ?? 0) + co.not_started);
    statuses.set('in_progress', (statuses.get('in_progress') ?? 0) + co.in_progress);
    statuses.set('completed', (statuses.get('completed') ?? 0) + co.completed);
    groupTotals.set(key, (groupTotals.get(key) ?? 0) + co.enrolled);
    total += co.enrolled;
  }

  return twoRingFromGroupStatus(byGroupStatus, groupTotals, total);
}

/** One workshop-status row: sessions, no-opportunity count, and attributed $ for that lifecycle state. */
export interface WorkshopPipelineRow {
  status: WorkshopScheduleStatus;
  label: string;
  count: number;
  amount: number;
  noOppCount: number;
}

/**
 * Pipeline $ **and** delivery counts bucketed by the delivery's derived **Webex workshop status**
 * (Not Scheduled / Scheduled / In-Progress / Complete) rather than its admin-set delivery status. This
 * is the single combined surface that replaces the separate delivery-status pipeline table and the
 * workshop-schedule donut: it answers "how far along is each session, and how much pipeline sits at each
 * stage". Each delivery contributes to exactly one row via {@link deliveryWorkshopStatus}; a session
 * with no linked opportunity adds 0 to `amount` and increments `noOppCount`.
 */
export function workshopPipelineRows(
  deliveries: CampaignDelivery[],
  meetingsByDelivery: Map<number, CampaignWorkshopMeeting[]>,
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>,
  amountByOpp: Map<string, number>,
  now: number = Date.now(),
): WorkshopPipelineRow[] {
  const rows = new Map<WorkshopScheduleStatus, WorkshopPipelineRow>(
    WORKSHOP_SCHEDULE_STATUSES.map((s) => [s.value, { status: s.value, label: s.label, count: 0, amount: 0, noOppCount: 0 }]),
  );
  for (const d of deliveries) {
    const status = deliveryWorkshopStatus(meetingsByDelivery.get(d.id) ?? [], now);
    const row = rows.get(status);
    if (!row) continue;
    row.count += 1;
    row.amount += deliveryPipeline(d, targetsByDelivery, amountByOpp);
    const targets = targetsByDelivery.get(d.id) ?? [];
    if (!targets.some((t) => !!t.opportunity)) row.noOppCount += 1;
  }
  return WORKSHOP_SCHEDULE_STATUSES.map((s) => rows.get(s.value) as WorkshopPipelineRow);
}
